import {
  createElement,
  type DetailedHTMLProps,
  forwardRef,
  type InputHTMLAttributes,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { cn } from "@repo/shared/utils";
import { type LucideIcon, ChevronDown, X, Check } from "lucide-react";
import { Label } from "./Label";
import { InputInfo } from "./InputInfo";
import { useNestedComponent } from "./hooks/useNestedComponent";

export interface ComboOption {
  value: string;
  label?: string;
  disabled?: boolean;
}

export interface ComboInputProps
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  id: string;
  label?: React.ReactNode;
  inputStyle?: string;
  labelStyle?: string;
  containerStyle?: string;
  error?: boolean;
  errorMessage?: string;
  orientation?: "horizontal" | "vertical";
  icon?: LucideIcon;
  wrapper?: React.ComponentType<{ children: React.ReactNode }>;
  inserts?: { after?: React.ReactNode };
  options: ComboOption[];
  listId?: string;
  clearable?: boolean;
  showChevron?: boolean;
  openOnFocus?: boolean;
  openOnType?: boolean;
}

const DynamicIcon = ({ icon }: { icon: LucideIcon }) =>
  createElement(icon, {
    size: 20,
    className: "absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform",
  });

export const ComboInput = forwardRef<HTMLInputElement, ComboInputProps>(
  (
    {
      id,
      label,
      inputStyle,
      labelStyle,
      containerStyle,
      error,
      errorMessage,
      orientation = "vertical",
      icon,
      inserts,
      className,
      wrapper,
      value,
      defaultValue,
      onChange,
      options,
      clearable = true,
      placeholder,
      disabled,
      showChevron = true,
      openOnFocus = false,
      openOnType = false,
      ...props
    },
    ref
  ) => {
    const Wrapper = useNestedComponent(
      wrapper ??
        (({ children }: { children: React.ReactNode }) => <>{children}</>)
    );

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const list = useMemo(() => options.filter((o) => !o.disabled), [options]);
    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return list;

      return list.filter(
        (o) =>
          o.value.toLowerCase().includes(q) ||
          o.label?.toLowerCase().includes(q)
      );
    }, [list, query]);

    const currentText = (value ?? "") as string;

    const commitValue = (next: string) => {
      const el = inputRef.current;
      if (!el) return;

      // React overrides el.value's setter. Use original setter:
      const setter = Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        "value"
      )?.set;
      setter?.call(el, next);

      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));

      setQuery("");
      setOpen(false);
      requestAnimationFrame(() => el.focus());
    };

    return (
      <div
        className={cn(
          "flex gap-y-1 mt-1.5",
          orientation === "horizontal"
            ? "flex-row items-center gap-x-3"
            : "flex-col",
          containerStyle
        )}
      >
        {label && <Label className={labelStyle} label={label} id={id} />}

        <div className="flex flex-col gap-1 w-full">
          <Popover.Root open={open} onOpenChange={setOpen}>
            <div className="relative w-full">
              {icon && <DynamicIcon icon={icon} />}

              <Wrapper>
                <input
                  id={id}
                  // type="text"
                  ref={inputRef}
                  value={currentText}
                  defaultValue={defaultValue as string | undefined}
                  placeholder={placeholder}
                  disabled={disabled}
                  onFocus={() => {
                    if (openOnFocus && !disabled) setOpen(true);
                  }}
                  onChange={(e) => {
                    const next = e.currentTarget.value;
                    setQuery(next);
                    if (openOnType && !disabled) setOpen(true);
                    onChange?.(e);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") setOpen(true);
                    if (e.key === "Escape") setOpen(false);
                  }}
                  className={cn(
                    "flex text-form-foreground h-10 w-full rounded-lg border border-input bg-white placeholder:text-muted-foreground " +
                      "px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium " +
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    error &&
                      "border-destructive focus:accent-destructive border-2",
                    inputStyle,
                    className,
                    icon && "pl-8"
                  )}
                  {...props}
                />
              </Wrapper>

              {clearable && !!currentText && !disabled ? (
                <button
                  type="button"
                  aria-label="Clear"
                  className="absolute right-7 top-1/2 -translate-y-1/2 p-1 text-black hover:opacity-80"
                  onClick={(e) => {
                    e.preventDefault();
                    commitValue((defaultValue as string | undefined) ?? "");
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}

              {showChevron && (
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    aria-label="Toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-black"
                    disabled={disabled}
                  >
                    <ChevronDown className="h-4 w-4 pointer-events-none opacity-80" />
                  </button>
                </Popover.Trigger>
              )}
            </div>

            <Popover.Content
              align="end"
              alignOffset={-8}
              sideOffset={6}
              className={
                "z-50 w-[var(--radix-popover-trigger-width)] min-w-[16rem] rounded-md " +
                "border bg-popover text-popover-foreground shadow-md outline-none"
              }
            >
              <Command label="ComboInput" className="w-full">
                <Command.List className="max-h-64 overflow-auto p-1">
                  {filtered.length > 0 ? (
                    <Command.Group
                      className="[&_[cmdk-group-heading]]:p-1 [&_[cmdk-group-heading]]:pl-7 [&_[cmdk-group-heading]]:text-muted-foreground"
                      heading="Suggestions"
                    >
                      {filtered.map((o) => {
                        const labelText = o.label ?? o.value;
                        const selected = currentText === o.value;
                        return (
                          <Command.Item
                            key={o.value}
                            value={o.value}
                            onSelect={() => commitValue(o.value)}
                            className={cn(
                              "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                              "aria-selected:bg-accent aria-selected:text-accent-foreground"
                            )}
                          >
                            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                              {selected ? <Check className="h-4 w-4" /> : null}
                            </span>
                            <span className="pl-5">{labelText}</span>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  ) : (
                    <div className="py-2 text-center text-sm text-muted-foreground">
                      Nothing matches your input.
                    </div>
                  )}
                </Command.List>
              </Command>
            </Popover.Content>
          </Popover.Root>

          {error && errorMessage && <InputInfo info={errorMessage} />}
        </div>

        {inserts?.after}
      </div>
    );
  }
);

ComboInput.displayName = "ComboInput";
